import React, {useEffect, useState} from 'react'
import {Button, Form, Space, Tag} from "antd"
import {EditOutlined, UserAddOutlined, UserSwitchOutlined} from "@ant-design/icons"
import PageBuilder from "../../../components/panel/PageBuilder"
import UserChangeStatusModal from "../UserChangeStatusModal"
import ModalWithForm from "../ModalWithForm"
import {
    AdminType,
    AdminFilter,
    useChangeAdminStatusMutation,
    useCreateAdminMutation,
    useGetAdminsQuery,
    useUpdateAdminMutation, AdminInfoType
} from "../../../api/adminApi"
import AdminForm from "./AdminForm"
import {UserStatusInfo} from "../../../api/accountManagerApi"
import ExtendedTable from "../../../components/ExtendedTable"
import {statusFilterOptions} from "../accountManager/AccountManagers";
import {useFilterTable} from "../../../hooks/useFilterTable";

const Admins = () => {
    const [formCreate] = Form.useForm<AdminInfoType>()
    const [formUpdate] = Form.useForm<AdminInfoType>()
    const [formChangeStatus] = Form.useForm<UserStatusInfo>()

    const [isModalOpenCreate, setIsModalOpenCreate] = useState(false)
    const [isModalOpenUpdate, setIsModalOpenUpdate] = useState(false)
    const [isModalOpenChangeStatus, setIsModalOpenChangeStatus] = useState(false)

    const [create, resultsCreate] = useCreateAdminMutation()
    const [update, resultsUpdate] = useUpdateAdminMutation()
    const [changeStatus, resultsChangeStatus] = useChangeAdminStatusMutation()

    const [filter, setFilter] = useState<AdminFilter>({
        page: 1,
        name: '',
        lastname: '',
        email: '',
        status: null
    })

    const {data, isLoading, refetch} = useGetAdminsQuery(filter)

    const [
        filterByText,
        _,
        filterBySelect,
    ] = useFilterTable<AdminFilter>(filter, setFilter);

    const columns = [
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            ...filterByText('email')
        },
        {
            title: 'First Name',
            dataIndex: 'name',
            key: 'name',
            ...filterByText('name')
        },
        {
            title: 'Last Name',
            dataIndex: 'lastname',
            key: 'lastname',
            ...filterByText('lastname')
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            ...filterBySelect('status', statusFilterOptions, 'id', false)
        },
        {
            title: 'Company',
            dataIndex: 'company',
            key: 'company',
            render: (company: AdminType['company']) => (
                <Tag key={company.id} color="processing">{company.name}</Tag>
            )
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (_: void, user: AdminType) => (
                <>
                    {
                        user.status !== 'sign_up_email_confirmation' && (
                            <Button
                                onClick={() => {
                                    const {status, id} = user
                                    formChangeStatus.setFieldsValue({status, id})
                                    setIsModalOpenChangeStatus(true)
                                }}
                                type="link"
                                size="small"
                            >
                                <UserSwitchOutlined/>
                            </Button>
                        )
                    }

                    <Button
                        onClick={() => {
                            const {company: {id: company_id}, ...otherFields} = user
                            formUpdate.setFieldsValue({...otherFields, company_id})
                            setIsModalOpenUpdate(true)
                        }}
                        type="link"
                        size="small"
                    >
                        <EditOutlined/>
                    </Button>
                </>
            ),
        },
    ]

    useEffect(() => {
        refetch()
    }, [filter])

    return (
        <PageBuilder title="Admins" documentTitle="Admins">
            <Space style={{marginBottom: 20}}>
                <Button type="primary" onClick={() => setIsModalOpenCreate(true)}>
                    <UserAddOutlined/> Create Admin
                </Button>
            </Space>

            <ExtendedTable
                data={data}
                isLoading={isLoading}
                dataKey="items"
                columns={columns}
                onChange={e => setFilter(prevState => ({...prevState, page: e}))}
            />

            <ModalWithForm
                title={"Create Admin"}
                isOpen={isModalOpenCreate}
                setIsOpen={setIsModalOpenCreate}
                isLoading={resultsCreate.isLoading}
                submit={formCreate.submit}
            >
                <AdminForm
                    refetch={refetch}
                    setIsModalOpen={setIsModalOpenCreate}
                    form={formCreate}
                    onFinish={create}
                />
            </ModalWithForm>

            <ModalWithForm
                title={"Update Admin"}
                isOpen={isModalOpenUpdate}
                setIsOpen={setIsModalOpenUpdate}
                isLoading={resultsUpdate.isLoading}
                submit={formUpdate.submit}
            >
                <AdminForm
                    refetch={refetch}
                    setIsModalOpen={setIsModalOpenUpdate}
                    form={formUpdate}
                    onFinish={update}
                />
            </ModalWithForm>

            <UserChangeStatusModal
                form={formChangeStatus}
                changeStatus={changeStatus}
                isModalOpen={isModalOpenChangeStatus}
                setIsModalOpen={setIsModalOpenChangeStatus}
                isLoading={resultsChangeStatus.isLoading}
                refetch={refetch}
            />
        </PageBuilder>
    )
}

export default Admins
